import type { GameObj } from 'kaplay'
import type { KAPLAYCtx } from '../types.ts'
import { useLang } from '../providers/i18n/i18n.ts'
import { getTiledSize, parsePolygon, getFileCacheStr } from '../utils.ts'
import { useGameUiTextFrame } from '../objects/game-ui-text-frame.ts'

export function useFloor10(k: KAPLAYCtx) {
  const cacheStr = getFileCacheStr()
  k.loadSprite('overlay', `sprites/overlay.png?${cacheStr}`, {
    sliceX: 4,
    sliceY: 2,
    anims: {
      question: {
        frames: [0],
      },
    },
  })
  k.loadSprite('floor10', `sprites/floors/floor-10.png?${cacheStr}`)
  k.loadSprite('floor10Hover', `sprites/floors/floor-10--hover.png?${cacheStr}`)
  const collidersJson = k.loadJSON('floor10Colliders', `colliders/floor-10.parsed.json?${cacheStr}`)

  const { showTextFrame } = useGameUiTextFrame(k)
  const { getLangString } = useLang(k)

  function getQuestionObj() {
    return [
      k.sprite('overlay', { anim: 'question' }),
      k.area({ isSensor: true }),
      k.layer('background'),
      'sfx_emit_action',
      'floor_10_info',
      'info',
    ]
  }

  function setFloor10() {
    const floor = k.add()

    k.onLoad(() => {
      const collidersData = collidersJson.data

      floor.add([
        k.sprite('floor10', {
          width: getTiledSize(collidersData.width),
          height: getTiledSize(collidersData.height),
        }),
        k.layer('background'),
        'floor_10_background',
        'background',
      ])

      floor.add([
        k.sprite('floor10Hover', {
          width: getTiledSize(collidersData.width),
          height: getTiledSize(collidersData.height),
        }),
        k.layer('hover'),
        'floor_10_hover',
        'hover',
      ])

      collidersData.objects.forEach((obj: any) => {
        const poly = parsePolygon(k, obj)
        floor.add([k.pos(), k.area({ shape: poly }), k.body({ isStatic: true }), 'colliders'])
      })

      floor.add([
        k.level(
          [
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                     r           ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                 s               ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                       y                                             7           ',
            '                                      v                      9                   ',
            '                x                                                                ',
            '                                                                                 ',
            '                                                 c                     8         ',
            '                                       4                                         ',
            '                                           a                  p                  ',
            '                                                                                 ',
            '                                                                 5               ',
            '                               3        u  b                                     ',
            '                                                                                 ',
            '               2                                                                 ',
            '         z                                               u                       ',
            '                       o                             v                           ',
            '                           #                                                     ',
            '                                                                          q      ',
            '                    $                                             6         $    ',
            '                                                                                 ',
            '                           #                                                     ',
            '                                                             t           &       ',
            '                                                                                 ',
            '                                                                 e               ',
            '                           #                                                     ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
            '                           #                                                     ',
            '                                                                                 ',
            '                                                                                 ',
            '                                                                                 ',
          ],
          {
            tileWidth: getTiledSize(),
            tileHeight: getTiledSize(),
            tiles: {

              // Elevators
              '#': () => [
                k.rect(getTiledSize(), getTiledSize(1.5), { fill: false }),
                k.pos(k.vec2(getTiledSize(0.5), 0)),
                k.area({ isSensor: true }),
                'floor_10_elevator',
                'elevator',
              ],
              '&': () => [
                k.rect(getTiledSize(2), getTiledSize(1), { fill: false }),
                k.pos(k.vec2(0, getTiledSize(0.5))),
                k.area({ isSensor: true }),
                'floor_10_elevator',
                'elevator',
              ],
              '$': () => [
                k.rect(getTiledSize(2), getTiledSize(), { fill: false }),
                k.area({ isSensor: true }),
                'floor_10_stairs',
                'stairs',
              ],

              // Doors
              '2': () => [
                k.rect(getTiledSize(), getTiledSize(), { fill: false }),
                k.area({ isSensor: true }),
                'floor_10_door_to_wc',
                'floor_10_door',
                'door',
              ],
              '3': () => [
                k.rect(getTiledSize(0.5), getTiledSize(), { fill: false }),
                k.pos(getTiledSize(0.5), 0),
                k.area({ isSensor: true }),
                'floor_10_door_to_meeting_room_e',
                'floor_10_door',
                'door',
              ],
              '4': () => [
                k.rect(getTiledSize(0.5), getTiledSize(), { fill: false }),
                k.area({ isSensor: true }),
                'floor_10_door_to_focus_room',
                'floor_10_door',
                'door',
              ],
              '5': () => [
                k.rect(getTiledSize(0.5), getTiledSize(), { fill: false }),
                k.area({ isSensor: true }),
                'floor_10_door_to_treadmill_room',
                'floor_10_door',
                'door',
              ],
              '6': () => [
                k.rect(getTiledSize(2), getTiledSize(), { fill: false }),
                k.area({ isSensor: true }),
                'floor_10_door_to_wc_handicap',
                'floor_10_door',
                'door',
              ],
              '7': () => [
                k.rect(getTiledSize(), getTiledSize(), { fill: false }),
                k.area({ isSensor: true }),
                'floor_10_door_to_wc_female',
                'floor_10_door',
                'door',
              ],
              '8': () => [
                k.rect(getTiledSize(0.5), getTiledSize(), { fill: false }),
                k.area({ isSensor: true }),
                'floor_10_door_to_wc_male',
                'floor_10_door',
                'door',
              ],
              '9': () => [
                k.rect(getTiledSize(0.5), getTiledSize(), { fill: false }),
                k.area({ isSensor: true }),
                'floor_10_door_to_mothers_room',
                'floor_10_door',
                'door',
              ],
              a: () => [
                k.rect(getTiledSize(0.5), getTiledSize(), { fill: false }),
                k.pos(getTiledSize(0.5), 0),
                k.area({ isSensor: true }),
                'floor_10_door_to_meeting_room_g',
                'floor_10_door',
                'door',
              ],
              b: () => [
                k.rect(getTiledSize(0.5), getTiledSize(), { fill: false }),
                k.pos(getTiledSize(0.5), 0),
                k.area({ isSensor: true }),
                'floor_10_door_to_meeting_room_h',
                'floor_10_door',
                'door',
              ],
              c: () => [
                k.rect(getTiledSize(), getTiledSize(), { fill: false }),
                k.area({ isSensor: true }),
                'floor_10_door_to_meeting_room_i',
                'floor_10_door',
                'door',
              ],
              d: () => [
                k.rect(getTiledSize(0.5), getTiledSize(), { fill: false }),
                k.area({ isSensor: true }),
                'floor_10_door_to_meeting_room_c',
                'floor_10_door',
                'door',
              ],
              e: () => [
                k.rect(getTiledSize(0.5), getTiledSize(), { fill: false }),
                k.area({ isSensor: true }),
                'floor_10_door_to_meeting_room_f',
                'floor_10_door',
                'door',
              ],

              // Interactions
              z: () => [
                ...getQuestionObj(),
                'floor_10_info_workcafe',
              ],
              y: () => [
                ...getQuestionObj(),
                'floor_10_info_coffee_point',
              ],
              x: () => [
                ...getQuestionObj(),
                'floor_10_info_library',
              ],
              v: () => [
                ...getQuestionObj(),
                'floor_10_info_communication_workstations',
              ],
              u: () => [
                ...getQuestionObj(),
                'floor_10_info_semi_enclosed',
              ],
              t: () => [
                ...getQuestionObj(),
                'floor_10_info_lockers',
              ],
              s: () => [
                ...getQuestionObj(),
                'floor_10_info_pods',
              ],
              r: () => [
                ...getQuestionObj(),
                'floor_10_info_concentration_workstations',
              ],
              q: () => [
                ...getQuestionObj(),
                'floor_10_info_alt_access',
              ],
              p: () => [
                ...getQuestionObj(),
                'floor_10_info_comms_room',
              ],
              o: () => [
                ...getQuestionObj(),
                'floor_10_info_intro',
              ],
            },
          },
        ),
        k.layer('objects'),
        'floor_10',
        'floor',
      ])

      // Info
      k.onCollide('floor_10_info', 'player', (info: GameObj) => {
        if (info.tags.includes('floor_10_info_intro'))
          showTextFrame(getLangString('floor_10_info_intro'))
        else if (info.tags.includes('floor_10_info_workcafe'))
          showTextFrame(getLangString('workcafe_info'))
        else if (info.tags.includes('floor_10_info_library'))
          showTextFrame(getLangString('library_info'))
        else if (info.tags.includes('floor_10_info_coffee_point'))
          showTextFrame(getLangString('coffee_point_info'))
        else if (info.tags.includes('floor_10_info_comms_room'))
          showTextFrame(getLangString('comms_room_info'))
        else if (info.tags.includes('floor_10_info_lockers'))
          showTextFrame(getLangString('lockers_info'))
        else if (info.tags.includes('floor_10_info_pods'))
          showTextFrame(getLangString('pods_info'))
        else if (info.tags.includes('floor_10_info_semi_enclosed'))
          showTextFrame(getLangString('semi_enclosed_info'))
        else if (info.tags.includes('floor_10_info_concentration_workstations'))
          showTextFrame(getLangString('concentration_workstations_info'))
        else if (info.tags.includes('floor_10_info_communication_workstations'))
        showTextFrame(getLangString('communication_workstations_info'))
        else if (info.tags.includes('floor_10_info_alt_access'))
          showTextFrame(getLangString('alt_access_info'))
      })
      k.onCollideEnd('player', 'info', () => {
        showTextFrame()
      })

    })

    return floor
  }

  return {
    setFloor10,
  }
}
